/**
 * DOM helpers. Kept minimal — for anything stateful (modals, alerts) use
 * `core/modal` and `core/alert`.
 */

/**
 * Wait for an element with the given ID to appear in the DOM. Resolves with
 * the element, or null after `timeoutMs`.
 * @param {string} id
 * @param {number} [timeoutMs=2000]
 * @returns {Promise<HTMLElement | null>}
 */
export async function waitForElById(id, timeoutMs = 2000) {
  const existing = document.getElementById(id);
  if (existing) return existing;

  return new Promise((resolve) => {
    let resolved = false;
    const done = (/** @type {HTMLElement | null} */ el) => {
      if (resolved) return;
      resolved = true;
      observer.disconnect();
      clearTimeout(timer);
      resolve(el);
    };

    const observer = new MutationObserver(() => {
      const el = document.getElementById(id);
      if (el) done(el);
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    const timer = setTimeout(() => done(null), timeoutMs);
  });
}

/**
 * Wait for `document.readyState === 'complete'` (or fire immediately if already).
 * @returns {Promise<void>}
 */
export function whenDocumentReady() {
  if (document.readyState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const onReady = () => {
      if (document.readyState === 'complete') {
        document.removeEventListener('readystatechange', onReady);
        resolve();
      }
    };
    document.addEventListener('readystatechange', onReady);
  });
}

/**
 * Run a callback once the user has interacted with the page (pointerdown,
 * keydown, touchstart) or after `idleAfterMs` has elapsed — whichever first.
 * Used to defer heavy work like push notification registration until after
 * the first render.
 *
 * @param {() => void} callback
 * @param {number} [idleAfterMs=6000]
 */
export function runAfterFirstInteraction(callback, idleAfterMs = 6000) {
  let fired = false;
  const fire = () => {
    if (fired) return;
    fired = true;
    cleanup();
    try { callback(); } catch (e) { console.error('runAfterFirstInteraction:', e); }
  };
  const opts = /** @type {AddEventListenerOptions} */ ({ once: true, passive: true });
  const events = /** @type {const} */ (['pointerdown', 'keydown', 'touchstart']);
  for (const ev of events) document.addEventListener(ev, fire, opts);
  const timer = setTimeout(fire, idleAfterMs);
  function cleanup() {
    for (const ev of events) document.removeEventListener(ev, fire);
    clearTimeout(timer);
  }
}
